import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField } from '../../common/InputField';
import { useAppContext } from '../../../utils/context/app-context';
import { ChangePassInput } from '../../../types/user.types';
import Button from '../../common/Button';
import AuthService from '../../../services/auth.service';
import { ToastType } from '../../../types/common.types';
import { useState } from 'react';

const ChangePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .required('Current Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    // .matches(
    //   /[!@#$%^&*]/,
    //   'Password must contain at least one special character (!@#$%^&*)'
    // )
    .min(8, 'Min 8 characters are required.')
    .max(50)
    .trim(),
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    // .matches(
    //   /[!@#$%^&*]/,
    //   'Password must contain at least one special character (!@#$%^&*)'
    // )
    .min(8, 'Min 8 characters are required.')
    .max(50)
    .trim(),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), ''], 'Passwords must match')
    .required('Confirm password is required'),
});

const ChangePassword = () => {
  const { setToastInfo, userInfo } = useAppContext();
  const [showPassword, setShowPassword] = useState(false);
  const initialValues: ChangePassInput = {
    id: userInfo?._id ?? '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const changePassword = async (
    values: ChangePassInput,
    resetForm: () => void
  ) => {
    try {
      const response = await AuthService.changePassword(values);
      if (!response.data.error) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: 'Password changed successfully.',
        });
        resetForm();
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err: any) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: err?.response?.data?.message,
      });
    }
  };

  return (
    <div className="w-full pb-10">
      <div className="border border-[rgba(0,0,0,.15)] rounded-3xl p-10 mx-auto max-w-[600px] mt-10">
        <Formik
          initialValues={initialValues}
          validationSchema={ChangePasswordSchema}
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            await changePassword(values, resetForm);
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
          }) => {
            return (
              <Form>
                <div className="grid grid-flow-col gap-4">
                  <div className="mb-5">
                    <TextField
                      name="oldPassword"
                      label="Current Password"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.oldPassword}
                      required={true}
                      isTouched={touched.oldPassword}
                      value={values.oldPassword}
                      type="password"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <TextField
                    name="newPassword"
                    label="New Password"
                    placeholder=""
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.newPassword}
                    required={true}
                    isTouched={touched.newPassword}
                    value={values.newPassword}
                    type="password"
                  />
                </div>

                <div className="mb-5 relative">
                  <TextField
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder=""
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.confirmPassword}
                    isTouched={touched.confirmPassword}
                    value={values.confirmPassword}
                    required={true}
                    type={showPassword ? 'text' : 'password'}
                  />
                  <i
                    className={`${
                      showPassword ? 'icon-eye-close' : 'icon-eye'
                    } absolute top-[32px] right-[15px] text-xl cursor-pointer`}
                    onClick={() => setShowPassword(!showPassword)}
                  ></i>
                </div>

                <div className="text-center">
                  <Button
                    type="button"
                    disabled={!touched || !isValid}
                    onClick={() => handleSubmit()}
                  >
                    Change Password
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default ChangePassword;
